import { appendRailsParameter } from "#/shared/lib/rails-query";
import { Model } from "#/shared/models/model";
import {
  MAX_RETRIES,
  RETRY_BASE_DELAY_MS,
  RETRY_MAX_DELAY_MS,
  abortableSleep,
  isRetryableStatus,
  jitteredBackoff,
} from "#/shared/api/retry";
import { getApiHost } from "#/shared/api/api-host";
import { requestLocale } from "#/shared/lib/locale";
import {
  ContentType,
  HeaderName,
  HttpError,
  HttpStatus,
  REQUEST_TIMEOUT_MS,
  isRawBody,
  isSafeMethod,
  isWriteMethod,
} from "#/shared/api/http";

// Matches a URL that carries a scheme (http:, https:, data:, blob:, …).
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Fetch-based JSON API client. Builds requests against a base URL, attaches a
 * CSRF token on same-origin writes, parses responses into model instances, and
 * applies a per-request timeout plus a bounded retry budget for idempotent
 * (GET/HEAD) requests.
 */
export class ApiClient {
  #api;
  #baseURL;
  #model;
  #parse;
  #headers;
  #root;
  #timeout;
  #retries;
  #credentials;
  #sortParams;

  /**
   * @param {Object} [options]
   * @param {String} [options.api] - Value for the `api` query param appended to every request.
   * @param {String} [options.baseURL] - Base URL prepended to relative request paths.
   * @param {Function} [options.model] - Model class responses are parsed into. Defaults to `Model`.
   * @param {Boolean} [options.parse] - Whether to parse responses into model instances. Defaults to `true`; pass `false` to return the raw payload.
   * @param {Object} [options.headers] - Headers merged into every request.
   * @param {String} [options.root] - Response key the payload is unwrapped from before parsing.
   * @param {Number} [options.timeout] - Per-request timeout in milliseconds before the request aborts. `0` or any non-finite value disables the timeout.
   * @param {Number} [options.retries] - Maximum retry attempts for idempotent requests on transient failures.
   * @param {RequestCredentials|function(URL): RequestCredentials} [options.credentials] - Fetch credentials mode, or a function of the request URL that returns one; a per-request value wins.
   * @param {Boolean} [options.sortParams] - Whether to sort the query string by name. Defaults to `false`.
   */
  constructor(options = {}) {
    const {
      api,
      baseURL,
      model,
      parse = true,
      headers = {},
      root,
      timeout = REQUEST_TIMEOUT_MS,
      retries = MAX_RETRIES,
      credentials,
      sortParams = false,
    } = options ?? {};

    this.#api = api;
    this.#baseURL = baseURL ?? "";
    this.#model = model ?? Model;
    this.#parse = parse !== false;
    this.#headers = headers;
    this.#root = root;
    this.#timeout = Number(timeout);
    this.#retries = this.#normalizeRetries(retries);
    this.#credentials = credentials;
    this.#sortParams = sortParams;
  }

  #normalizeRetries(retries) {
    const value = Math.trunc(Number(retries));

    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  parse(data) {
    return new this.#model(data);
  }

  transform(data) {
    if (this.#root) {
      data = data?.[this.#root];
    }

    if (data === null || data === undefined) {
      return null;
    }

    if (!this.#parse) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((datum) => this.parse(datum));
    }
    return this.parse(data);
  }

  /** Returns the exact request URL, including API, Rails query encoding and locale. */
  urlFor(path = "", params = {}) {
    return this.#buildUrl(path, params).href;
  }

  /**
   * Sends a GET request with query parameters and returns the transformed payload.
   * @param {String} [path] - Request path, joined to the base URL unless absolute.
   * @param {Object|URLSearchParams} [params] - Query parameters.
   * @param {Object} [options] - Options forwarded to `request()`.
   * @param {String} [options.accept] - Required response `Content-Type`, also sent as the `Accept`
   *   header over any supplied `Accept` header. Mismatched successful responses are rejected
   *   with `HttpError`; 204 responses return `null`.
   * @returns {Promise<*>} The transformed response payload.
   */
  async get(path = "", params = {}, options = {}) {
    return this.request("get", path, { ...options, params });
  }

  async post(path, data) {
    return this.request("post", path, { data });
  }

  async put(path, data) {
    return this.request("put", path, { data });
  }

  async patch(path, data = {}) {
    return this.request("patch", path, { data });
  }

  async delete(path) {
    return this.request("delete", path);
  }

  /**
   * Sends a request and returns the parsed, transformed payload. Each attempt is
   * bounded by the instance timeout; idempotent (GET/HEAD) requests retry transient
   * failures (network errors, timeouts, 429/502/503/504) up to the retry cap with
   * jittered exponential backoff. Timeout and network exhaustion surface as `HttpError`.
   *
   * @param {String} method - HTTP method.
   * @param {String} [path] - Request path, joined to the base URL unless absolute.
   * @param {Object} [options]
   * @param {*} [options.data] - Request body; serialized to JSON unless already a raw body.
   * @param {Object} [options.headers] - Per-request headers merged over the instance headers.
   * @param {Object|URLSearchParams} [options.params] - Query parameters.
   * @param {String} [options.accept] - Required response `Content-Type`, also sent as the `Accept`
   *   header over any supplied `Accept` header. Mismatched successful responses are rejected
   *   before parsing, without retrying. 204 responses return `null`.
   * @param {AbortSignal} [options.signal] - Caller signal; aborting it cancels the request and any pending retry backoff.
   * @param {RequestCredentials} [options.credentials] - Fetch credentials mode; defaults to the instance mode, then the browser's same-origin mode.
   * @returns {Promise<*>} The transformed response payload.
   * @throws {HttpError} On non-success responses, mismatched content types, timeouts, and network failures.
   */
  async request(method, path = "", options = {}) {
    const data = await this.#fetchWithRetry(method, path, options);

    return this.transform(data);
  }

  /**
   * Sends a request and returns the raw `Response` for the caller to stream.
   * The request is built like `request()` — base URL, instance headers, CSRF
   * token on same-origin writes — but the body is left unconsumed. The instance
   * timeout bounds the wait for response headers only; once the `Response` is
   * returned, the open body stream is cancelled through `options.signal`.
   * Streams never retry.
   *
   * @param {String} method - HTTP method.
   * @param {String} [path] - Request path, joined to the base URL unless absolute.
   * @param {Object} [options]
   * @param {*} [options.data] - Request body; serialized to JSON unless already a raw body.
   * @param {Object} [options.headers] - Per-request headers merged over the instance headers.
   * @param {Object|URLSearchParams} [options.params] - Query parameters.
   * @param {String} [options.accept] - Required response `Content-Type`, also sent as the `Accept`
   *   header. A response of any other type — an auth redirect landing on an HTML page, for
   *   example — is drained and rejected instead of handed to the stream reader.
   * @param {AbortSignal} [options.signal] - Caller signal; aborting it cancels the request and the open stream.
   * @param {RequestCredentials} [options.credentials] - Fetch credentials mode; defaults to the instance mode, then the browser's same-origin mode.
   * @returns {Promise<Response>} A successful response with a readable body.
   * @throws {HttpError} On non-success responses, mismatched content types, timeouts, and network failures.
   */
  async stream(method, path = "", options = {}) {
    const { signal: callerSignal, accept, headers, ...requestOptions } = options ?? {};
    const controller = new AbortController();
    const timer = this.#startTimeout(controller);
    // The link outlives the call, so the caller signal still cancels the open body.
    // AbortSignal.any sits above the browserslist floor and nothing backfills it —
    // acceptable while stream()'s consumers are admin-only.
    const signal = callerSignal ? AbortSignal.any([callerSignal, controller.signal]) : controller.signal;

    try {
      const requestHeaders = this.#buildStreamHeaders(accept, headers);
      const request = this.#buildRequest(method, path, {
        ...requestOptions,
        headers: requestHeaders,
        signal,
      });
      const response = await fetch(request);

      await this.#assertStreamable(response, accept);

      return response;
    } catch (error) {
      throw this.#toError(error);
    } finally {
      clearTimeout(timer);
    }
  }

  #buildStreamHeaders(accept, headers) {
    const streamHeaders = { ...headers };

    if (accept) {
      streamHeaders[HeaderName.ACCEPT] = accept;
    }

    return streamHeaders;
  }

  async #assertStreamable(response, accept) {
    if (!response.ok) {
      throw this.#buildError(response, await this.#parseBody(response));
    }

    await this.#assertContentType(response, accept);

    if (!response.body) {
      throw new HttpError("Response has no readable body", { status: response.status });
    }
  }

  async #assertContentType(response, accept) {
    const contentType = response.headers.get(HeaderName.CONTENT_TYPE) ?? "";

    if (accept && !contentType.includes(accept)) {
      await this.#discardBody(response);
      throw new HttpError(`Expected a ${accept} response, got ${contentType || "no content type"}`, {
        status: response.status,
      });
    }
  }

  async #fetchWithRetry(method, path, options = {}) {
    const { signal: callerSignal, ...requestOptions } = options ?? {};
    const maxAttempts = isSafeMethod(method) ? this.#retries + 1 : 1;

    for (let attempt = 1; ; attempt++) {
      const outcome = await this.#runAttempt(method, path, requestOptions, callerSignal, attempt < maxAttempts);

      if (!outcome.retry) {
        return outcome.data;
      }

      await this.#waitBeforeRetry(attempt, callerSignal);
    }
  }

  async #runAttempt(method, path, options, callerSignal, canRetry) {
    try {
      return await this.#attempt(method, path, options, callerSignal, { allowStatusRetry: canRetry });
    } catch (error) {
      if (this.#callerAborted(callerSignal)) {
        throw error;
      }

      if (canRetry && this.#isRetryableError(error)) {
        return { retry: true };
      }

      throw this.#toError(error);
    }
  }

  async #attempt(method, path, options, callerSignal, { allowStatusRetry }) {
    const controller = new AbortController();
    const release = this.#linkAbort(controller, callerSignal);

    try {
      const request = this.#buildRequest(method, path, { ...options, signal: controller.signal });
      const response = await fetch(request);

      if (allowStatusRetry && isRetryableStatus(response.status)) {
        await this.#discardBody(response);
        return { retry: true };
      }

      return { data: await this.#handleResponse(response, options.accept) };
    } finally {
      release();
    }
  }

  #linkAbort(controller, callerSignal) {
    const timer = this.#startTimeout(controller);
    const onCallerAbort = () => controller.abort(callerSignal.reason);

    if (callerSignal?.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
    }

    return () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", onCallerAbort);
    };
  }

  #startTimeout(controller) {
    if (!Number.isFinite(this.#timeout) || this.#timeout <= 0) {
      return undefined;
    }

    const timeout = new DOMException(`Request timed out after ${this.#timeout}ms`, "TimeoutError");

    return setTimeout(() => controller.abort(timeout), this.#timeout);
  }

  async #discardBody(response) {
    try {
      await response.body?.cancel();
    } catch {
      // body already settled
    }
  }

  #callerAborted(callerSignal) {
    return Boolean(callerSignal?.aborted);
  }

  #isRetryableError(error) {
    return error?.name === "TimeoutError" || error instanceof TypeError;
  }

  #toError(error) {
    if (error?.name === "TimeoutError") {
      return new HttpError(`Request timed out after ${this.#timeout}ms`, { cause: error });
    }

    if (error instanceof TypeError) {
      return new HttpError(error.message || "Network request failed", { cause: error });
    }

    return error;
  }

  async #waitBeforeRetry(attempt, callerSignal) {
    const delay = jitteredBackoff(attempt - 1, { baseMs: RETRY_BASE_DELAY_MS, maxMs: RETRY_MAX_DELAY_MS });

    await abortableSleep(delay, callerSignal);
  }

  #requestCredentials(url, override) {
    if (override !== undefined && override !== null) {
      return override;
    }

    return typeof this.#credentials === "function" ? this.#credentials(url) : this.#credentials;
  }

  #buildRequest(method, path, { data, headers, params, signal, credentials, accept } = {}) {
    const url = this.#buildUrl(path, params);
    const requestHeaders = this.#buildHeaders(method, url, headers);

    if (accept) {
      requestHeaders.set(HeaderName.ACCEPT, accept);
    }

    const body = this.#buildBody(data, requestHeaders);

    return new Request(url, {
      body,
      headers: requestHeaders,
      method: method.toUpperCase(),
      signal,
      credentials: this.#requestCredentials(url, credentials),
    });
  }

  #buildUrl(path, params) {
    const baseURL = this.#baseURLWithApiHost();
    let requestPath = path || baseURL;

    // Absolute URLs (capture URLs, data: URIs, etc.) are used as-is, not joined to apiHost/baseURL.
    if (baseURL && path && !this.#isAbsoluteUrl(path)) {
      requestPath = `${baseURL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
    }

    const url = new URL(requestPath, location.origin);

    for (const [key, value] of this.#buildParams(params)) {
      url.searchParams.append(key, value);
    }

    // Forward the page locale so server-rendered strings in the payload match
    // the page language. The param is the only carrier, since a request names
    // its locale by path prefix or param alone. The default locale resolves to
    // null (URLs stay param-free and cacheable), and a locale the caller set
    // explicitly (param or path) wins. Absolute request URLs (third-party
    // capture URLs, data: URIs) are never decorated — only requests routed
    // through the client's own base URL are Sparkle-bound by construction.
    const locale = path && this.#isAbsoluteUrl(path) ? null : requestLocale();
    if (locale && !url.searchParams.has("locale")) {
      url.searchParams.append("locale", locale);
    }

    if (this.#sortParams) {
      url.searchParams.sort();
    }

    return url;
  }

  #isAbsoluteUrl(path) {
    return ABSOLUTE_URL_PATTERN.test(path);
  }

  #baseURLWithApiHost() {
    if (this.#baseURL.startsWith("http")) {
      return this.#baseURL;
    }

    const { apiHost } = getApiHost();

    if (apiHost) {
      return new URL(this.#baseURL, apiHost).href;
    }

    return this.#baseURL;
  }

  #buildParams(params) {
    const searchParams = new URLSearchParams();

    if (this.#api) {
      searchParams.append("api", this.#api);
    }

    if (!params) {
      return searchParams;
    }

    if (params instanceof URLSearchParams) {
      for (const [key, value] of params) {
        searchParams.append(key, value);
      }

      return searchParams;
    }

    for (const [key, value] of Object.entries(params)) {
      this.#appendParam(searchParams, key, value);
    }

    return searchParams;
  }

  #appendParam(searchParams, key, value) {
    appendRailsParameter(searchParams.append.bind(searchParams), key, value, { skip: isNullish });
  }

  #buildHeaders(method, url, headers = {}) {
    const requestHeaders = new Headers({
      [HeaderName.ACCEPT]: ContentType.JSON,
      ...this.#headers,
      ...headers,
    });

    this.#applyCsrfToken(requestHeaders, method, url);
    return requestHeaders;
  }

  #applyCsrfToken(headers, method, url) {
    if (!isWriteMethod(method) || url.origin !== location.origin || headers.has(HeaderName.CSRF_TOKEN)) {
      return;
    }

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    if (token) {
      headers.set(HeaderName.CSRF_TOKEN, token);
    }
  }

  #buildBody(data, headers) {
    if (data === undefined) {
      return undefined;
    }

    if (isRawBody(data)) {
      return data;
    }

    if (!headers.has(HeaderName.CONTENT_TYPE)) {
      headers.set(HeaderName.CONTENT_TYPE, ContentType.JSON);
    }

    return JSON.stringify(data);
  }

  async #handleResponse(response, accept) {
    if (response.status === HttpStatus.NO_CONTENT) {
      return null;
    }

    if (response.ok) {
      await this.#assertContentType(response, accept);
    }

    const data = await this.#parseBody(response);

    if (!response.ok) {
      throw this.#buildError(response, data);
    }

    return data;
  }

  async #parseBody(response) {
    const text = await response.text();

    if (!text) {
      return null;
    }

    const contentType = response.headers.get(HeaderName.CONTENT_TYPE) ?? "";

    if (contentType.includes(ContentType.JSON) || contentType.includes("+json")) {
      try {
        return JSON.parse(text);
      } catch (error) {
        if (!response.ok) {
          return text;
        }

        throw error;
      }
    }

    return text;
  }

  #buildError(response, data) {
    return new HttpError(this.#errorMessageForResponse(response, data), {
      data,
      status: response.status,
    });
  }

  #errorMessageForResponse(response, data) {
    if (typeof data?.error === "string" && data.error.length > 0) {
      return data.error;
    }

    if (typeof data === "string" && data.trim().length > 0 && !data.trim().startsWith("<")) {
      return data;
    }

    return response.statusText || `Request failed with status ${response.status}`;
  }
}

/**
 * Builds an ApiClient. Prefer this over `new ApiClient` at call sites.
 */
export function createApiClient(options) {
  return new ApiClient(options);
}

function isNullish(value) {
  return value === null || value === undefined;
}
